import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getAbandonedCarts } from '../../utils/statisticsHelpers';

interface AbandonedCartsWidgetProps {
  data: StatisticsSummaryView | null;
}

const COLOR_ABANDONED = '#DC2626';
const COLOR_CONFIRMED = '#059669';

/**
 * ТЗ п.6: Abandoned Carts - donut chart showing abandoned vs confirmed.
 * abandonedCartsPercent = abandonedCartsCount / totalCartsCount * 100
 */
export const AbandonedCartsWidget: React.FC<AbandonedCartsWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const carts = getAbandonedCarts(data);

  const abandoned = carts?.abandonedCartsCount ?? 0;
  const confirmed = carts?.confirmedCartsCount ?? 0;
  const total = carts?.totalCartsCount ?? 0;
  const percent = carts?.abandonedCartsPercent ?? 0;

  const chartData = React.useMemo(() => {
    if (total === 0) return [];
    return [
      { name: t('dashboard_stats_abandoned'), value: abandoned, color: COLOR_ABANDONED },
      { name: t('dashboard_stats_confirmed'), value: confirmed, color: COLOR_CONFIRMED },
    ].filter((d) => d.value > 0);
  }, [abandoned, confirmed, total, t]);

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_abandoned_carts')}</h3>
      </div>
      <div className="stat-metrics-row">
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_abandoned_percent')}</span>
          <span className="stat-metric-value stat-metric-value--lg">{percent.toFixed(1)}%</span>
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_total_carts')}</span>
          <span className="stat-metric-value">{total}</span>
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_abandoned')}</span>
          <span className="stat-metric-value">{abandoned}</span>
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_confirmed')}</span>
          <span className="stat-metric-value">{confirmed}</span>
        </div>
      </div>
      {chartData.length > 0 && (
        <div className="chart-container chart-container--sm">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="75%"
                paddingAngle={2}
                label={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      {chartData.length === 0 && total === 0 && (
        <p className="stat-metric-label">{t('dashboard_stats_no_data')}</p>
      )}
    </div>
  );
};
